import store from '@state/store'

/**
 * Módulo Vuex principal do chat. Serviços citados e painel vivem SEMPRE aqui,
 * mesmo quando a instância de `APIChat` tem namespace próprio: são canais de
 * turno lidos por componentes que não conhecem a superfície que os produziu.
 */
export const STORE_PRINCIPAL = 'aawz-intelligence-v2'

/**
 * Mapa `evento SSE → handler` do pipeline de chat.
 *
 * Vive fora de `apiChat.js` porque aquele arquivo já passava do teto de linhas
 * do repo — registrar evento novo lá dentro o empurrava a cada feature. Aqui a
 * instância entra por parâmetro: o mapa continua sendo só despacho, sem estado
 * próprio.
 *
 * Evento que não estiver neste mapa é ignorado por `_processEvent` — é o que
 * mantém um frontend antigo funcionando quando o backend ganha um evento novo.
 *
 * ## Os dois vocabulários
 *
 * O backend faz DUAL-EMIT (spec-001 do `project-plataforma-por-chat`, T010): o
 * mesmo fato sai no nome antigo (`token`, `thinking`) e no tipado
 * (`text-delta`, `reasoning-delta`). Os dois nomes caem em UMA função por fato
 * (`_onDeltaDeTexto`, `_onDeltaDeRaciocinio`), que descarta a duplicata pela
 * constante de fonte — dois handlers independentes escreveriam o mesmo delta
 * duas vezes, que é exatamente o defeito que a constante existe para impedir.
 *
 * `start` e `error` mantêm o nome nos dois vocabulários: os campos novos
 * (`persona`/`prompt_version`/`model`, `traceback_id`) viajam nos mesmos
 * handlers, que ignoram o que não leem.
 *
 * @param {Object} instance Instância de `APIChat`.
 * @returns {Object} Mapa de handlers por nome de evento.
 */
export function getEventHandlers(instance) {
  const ns = instance.storeNamespace
  return {
    // ---- vocabulário atual (a fonte da verdade enquanto o dual-emit durar)
    start: function (data) {
      instance._onEventStart(data)
    },
    thinking: function (data) {
      instance._onDeltaDeRaciocinio('thinking', data)
    },
    tool_call: function (data) {
      store.dispatch(ns + '/addToolCall', data.tool || '')
    },
    tool_result: function (data) {
      store.dispatch(ns + '/removeToolCall', data.tool || '')
      instance._maybeCapturePendingConfirmation(data)
      instance._maybeCaptureServicosCitados(data)
      instance._maybeCapturePerguntas(data)
    },
    token: function (data) {
      instance._onDeltaDeTexto('token', data)
    },
    // Incrementos da deliberação (abertura, status, fala, parecer, rodada,
    // encerramento) e a transcrição final. Delegam aos métodos da instância —
    // o dedupe por `seq` do `painel_evento` precisa de estado (`_painelSeqVisto`),
    // que não cabe num handler solto.
    //
    // ★ Depois do merge com o `desenvolvimento` (#1961) o store guarda a
    // transcrição CRUA: quem reduz é `painelParcialStreaming` (getter) e o
    // próprio `painel-deliberacao`. O adaptador que existia aqui saiu junto com
    // `utils/painel-eventos.js`.
    painel_evento: function (data) {
      instance._onPainelEvento(data)
    },
    painel: function (data) {
      instance._capturePainel(data)
    },
    // Os passos de negócio do Progress da preparação de reunião e demais
    // atalhos (spec-001 `preparacao-reuniao`, D8.2, `core/plano_vivo.py`).
    // `versao` só cresce — o dedupe contra evento atrasado mora em cada
    // handler, não aqui: este mapa é só despacho.
    //
    // ⚠️ [merge 21/09/2026] O MESMO evento tem DOIS consumidores, e a chave
    // estava DUPLICADA neste objeto literal (`no-dupe-keys`): em JS a última
    // vence, então `_onPlano` — que alimenta `planoDoTurno`, o plano que o
    // `AssistantBlocks` desenha e que o `end` persiste na mensagem — morria
    // em silêncio, e só `_onEventPlano` (o canal `plano` do cartão de
    // progresso/encenação, no namespace da CONVERSA) rodava. Um handler, os
    // dois despachos; cada um mantém o próprio dedupe por `versao`.
    plano: function (data) {
      instance._onPlano(data)
      instance._onEventPlano(data)
    },
    end: function (data) {
      instance._onEventEnd(data)
    },
    error: function (data) {
      instance._onEventError(data)
    },

    // ---- vocabulário tipado (spec-001 do `project-plataforma-por-chat`)
    'text-delta': function (data) {
      instance._onDeltaDeTexto('text-delta', data)
    },
    'reasoning-delta': function (data) {
      instance._onDeltaDeRaciocinio('reasoning-delta', data)
    },
    'tool-input-available': function (data) {
      instance._onToolInputAvailable(data)
    },
    'tool-approval-request': function (data) {
      instance._onToolApprovalRequest(data)
    },
    'tool-output-available': function (data) {
      instance._onToolOutputAvailable(data)
    },
    'tool-output-error': function (data) {
      instance._onToolOutputError(data)
    },
    'data-painel': function (data) {
      instance._onDataPainel(data)
    },
    finish: function (data) {
      instance._onFinish(data)
    },

    // ---- voz v3 (08/09) — a versão FALADA do turno, separada do texto
    // escrito. Um evento por turno, só quando `modo_voz: true` foi enviado
    // no corpo (`_buildChatBody`). Sem par no vocabulário antigo: `token`
    // continua sendo o texto ESCRITO de sempre. Ver o cabeçalho de
    // `fala-da-ia.js` pra a ordem de precedência com o fallback.
    fala: function (data) {
      instance._onEventFala(data)
    },

    // ---- D6 — o agente por turno (spec-010, [T028]) — ADITIVOS, sem par no
    // vocabulário antigo (`core/sse.py` não os inclui no dual-emit: não há
    // fato antigo equivalente a rotear por agente).
    'agent-selected': function (data) {
      instance._onAgentSelected(data)
    },
    'node-status': function (data) {
      instance._onNodeStatus(data)
    },

    // ---- cliente em foco pela conversa (spec-009 [T006]) — ADITIVO, sem
    // par no vocabulário antigo (a resolução de cliente do turno nunca teve
    // fato equivalente no fio antes desta spec).
    'cliente-identificado': function (data) {
      instance._onClienteIdentificado(data)
    },

    // ---- Modo Agente (spec-002, D13) — ADITIVOS, sem par no vocabulário
    // antigo: o relay agêntico (`relay.py::_frames_do_evento`) é uma fonte
    // NOVA de eventos, não um dual-emit do fio de chat.
    //
    // ★ merge PR #2242 — estes seis viviam no mapa que a spec-002 montava
    // dentro de `apiChatStream.js::_getEventHandlers`, e esse mixin deixou de
    // ser aplicado no merge (11 dos 15 métodos dele colidiam com a classe, que
    // evoluiu do outro lado). Sem registrá-los AQUI o run agêntico roda e a
    // tela não reage a nada: `_processEvent` ignora em silêncio qualquer evento
    // fora deste mapa — que é o comportamento certo para frontend antigo ×
    // backend novo, e é exatamente por isso que a perda não daria erro nenhum.
    //
    // Os handlers moram no mixin `apiChatModoAgenteEventos.js`, que continua
    // aplicado (zero colisões com a classe). `busca`/`codigo`/`arquivo` só
    // REGISTRAM nos canais transientes — quem os desenha é outra task.
    estado: function (data) {
      instance._onEventEstado(data)
    },
    // `plano` NÃO se repete aqui: o relay emite o MESMO evento SSE que o fio
    // de chat, e o registro único acima chama os DOIS consumidores
    // (`_onPlano` → `planoDoTurno` no store fixo; `_onEventPlano` → `plano`
    // no namespace da CONVERSA). A segunda chave que o merge criou (review
    // Gandalf, PR #2328) vencia no literal e deixava `_onPlano` inalcançável
    // — em silêncio.
    decisao: function (data) {
      instance._onEventDecisao(data)
    },
    busca: function (data) {
      // [spec-002 T051b/MUST-1] guarda na INSTÂNCIA (sobrevive ao PRÓXIMO
      // turno, que reseta o canal transiente do store) além de despachar pro
      // canal transiente de sempre — ver `_registrarBuscaWebDoTurno`.
      instance._registrarBuscaWebDoTurno(data)
      instance._dispatchNoStoreDoAgente('adicionarBuscaStreaming', data)
    },
    codigo: function (data) {
      instance._dispatchNoStoreDoAgente('adicionarCodigoStreaming', data)
    },
    arquivo: function (data) {
      instance._dispatchNoStoreDoAgente('adicionarArquivoStreaming', data)
    },
  }
}
